/**
 * CivicPulse — Issue detail page.
 *
 * Photo + map, status timeline, "I am also affected", follow, comment.
 * Moderator/authority-only status-update control appears at the bottom
 * (best-effort — surfaces even if unauthenticated, backend enforces role).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  MapPin, ChevronLeft, ThumbsUp, Bell, Share2, Loader2, CheckCircle2,
  AlertTriangle, MessageSquare, Clock, X, Copy,
} from "lucide-react";
import { toast } from "sonner";
import CivicMap from "@/components/civic/CivicMap";
import { civic, photoUrl } from "./api";
import { civicPath } from "./basePath";

const STATUS_STYLES = {
  reported:            { color: "#0B2545", label: "Reported" },
  under_review:        { color: "#6366F1", label: "Under review" },
  routed:              { color: "#0891B2", label: "Routed to authority" },
  acknowledged:        { color: "#0891B2", label: "Acknowledged" },
  work_underway:       { color: "#EAB308", label: "Work underway" },
  claimed_resolved:    { color: "#16A34A", label: "Claimed resolved" },
  community_verified:  { color: "#0F7B3F", label: "✓ Community verified" },
  reopened:            { color: "#DC2626", label: "Reopened" },
  closed:              { color: "#0F7B3F", label: "Closed" },
};

export default function CivicIssueDetail() {
  const { publicId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [affecting, setAffecting] = useState(false);
  const [following, setFollowing] = useState(false);
  const [comment, setComment] = useState({ author_name: "", text: "" });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: "", note: "" });
  const [shareOpen, setShareOpen] = useState(false);
  const [shareData, setShareData] = useState(null);

  const refresh = () => civic.getIssue(publicId).then(setData);

  useEffect(() => {
    if (!publicId) return;
    setLoading(true);
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF7]">
        <Loader2 className="w-6 h-6 text-emerald-700 animate-spin" />
      </div>
    );
  }
  if (!data?.issue) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl text-slate-900 mb-2">Issue not found</h1>
          <p className="text-slate-600">The permanent CivicPulse ID <b>{publicId}</b> doesn't match any report.</p>
          <Link to={civicPath("/")} className="mt-6 inline-block text-emerald-700 underline hover:no-underline">
            Return home
          </Link>
        </div>
      </div>
    );
  }
  const { issue, timeline = [], comments = [] } = data;
  const statusStyle = STATUS_STYLES[issue.status] || STATUS_STYLES.reported;

  async function markAffected() {
    setAffecting(true);
    try {
      const r = await civic.markAffected(publicId);
      toast.success(`Marked as affected — ${r.affected_count} people affected in total.`);
      refresh();
    } catch (e) { toast.error(e.message); }
    finally { setAffecting(false); }
  }

  async function toggleFollow() {
    setFollowing(true);
    try {
      const r = await civic.toggleFollow(publicId);
      toast.success(r.following ? "Following. You'll receive updates." : "Unfollowed.");
      refresh();
    } catch (e) { toast.error(e.message === "Sign in to follow an issue" ? "Please sign in to follow an issue." : e.message); }
    finally { setFollowing(false); }
  }

  async function shareIssue() {
    setShareOpen(true);
    if (shareData) return;
    try {
      const r = await civic.share(publicId);
      setShareData(r);
    } catch (e) {
      toast.error(e.message);
      setShareOpen(false);
    }
  }

  async function submitComment() {
    if (!comment.text.trim()) return;
    setSubmittingComment(true);
    try {
      await civic.addComment(publicId, comment);
      setComment({ author_name: comment.author_name, text: "" });
      refresh();
    } catch (e) { toast.error(e.message); }
    finally { setSubmittingComment(false); }
  }

  async function submitStatus() {
    if (!statusForm.status) return;
    try {
      await civic.updateStatus(publicId, statusForm);
      toast.success("Status updated.");
      setStatusForm({ status: "", note: "" });
      refresh();
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="civic-issue-detail">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link to={civicPath("/map")} className="text-sm text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 mb-3">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to map
        </Link>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div>
            {issue.photo_urls?.[0] && (
              <img src={photoUrl(issue.photo_urls[0])} alt=""
                   className="w-full max-h-[480px] object-cover rounded-2xl ring-1 ring-emerald-100 mb-4" />
            )}
            <div className="text-[11px] uppercase tracking-widest font-semibold text-emerald-700">
              {issue.category}
            </div>
            <h1 className="mt-1 font-serif text-3xl sm:text-4xl font-bold text-slate-900">
              {issue.title}
            </h1>
            <div className="mt-3 flex items-center gap-3 text-sm text-slate-600 flex-wrap">
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4 text-emerald-700" /> {issue.location?.address || issue.location?.city || issue.location?.district || "Kerala"}</span>
              <span className="text-slate-300">•</span>
              <span className="font-mono text-xs text-slate-500">{issue.public_id}</span>
              <span className="text-slate-300">•</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: `${statusStyle.color}18`, color: statusStyle.color }}>
                <span className="w-2 h-2 rounded-full" style={{ background: statusStyle.color }} />
                {statusStyle.label}
              </span>
            </div>
            {issue.description && (
              <p className="mt-5 text-slate-700 leading-relaxed">{issue.description}</p>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={markAffected} disabled={affecting}
                      data-testid="civic-affected-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-4 py-2 text-sm transition disabled:opacity-70">
                <ThumbsUp className="w-4 h-4" /> I am also affected ({issue.affected_count || 0})
              </button>
              <button onClick={toggleFollow} disabled={following}
                      data-testid="civic-follow-btn"
                      className="inline-flex items-center gap-2 rounded-full ring-1 ring-emerald-700 text-emerald-800 hover:bg-emerald-50 font-semibold px-4 py-2 text-sm transition">
                <Bell className="w-4 h-4" /> Follow ({issue.follow_count || 0})
              </button>
              <button onClick={shareIssue}
                      data-testid="civic-share-btn"
                      className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 text-slate-700 hover:bg-slate-50 font-semibold px-4 py-2 text-sm transition">
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>

            {/* Status timeline */}
            <section className="mt-10">
              <h2 className="font-serif text-2xl font-bold text-slate-900 mb-3">
                <Clock className="inline w-5 h-5 -mt-1 mr-1.5 text-emerald-700" /> Status timeline
              </h2>
              <ol className="border-l-2 border-emerald-200 ml-3 space-y-4 pl-5">
                {timeline.map((ev, i) => {
                  const s = STATUS_STYLES[ev.status] || STATUS_STYLES.reported;
                  return (
                    <li key={i} className="relative" data-testid={`civic-timeline-${i}`}>
                      <span className="absolute -left-[29px] top-1 w-4 h-4 rounded-full ring-4 ring-white"
                            style={{ background: s.color }} />
                      <div className="text-sm font-semibold" style={{ color: s.color }}>{s.label}</div>
                      {ev.note && <div className="text-sm text-slate-700 mt-0.5">{ev.note}</div>}
                      <div className="text-xs text-slate-500 mt-0.5">
                        {new Date(ev.created_at).toLocaleString()}{" • "}{ev.actor_role || "system"}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>

            {/* Comments */}
            <section className="mt-10">
              <h2 className="font-serif text-2xl font-bold text-slate-900 mb-3">
                <MessageSquare className="inline w-5 h-5 -mt-1 mr-1.5 text-emerald-700" />
                Discussion ({comments.length})
              </h2>
              <div className="space-y-3">
                {comments.map((c) => (
                  <div key={c.id} className="rounded-xl bg-white ring-1 ring-emerald-100 p-3 text-sm">
                    <div className="font-semibold text-slate-800">{c.author_name}</div>
                    <div className="text-slate-700 mt-0.5">{c.text}</div>
                    <div className="text-xs text-slate-500 mt-1">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <div className="text-sm text-slate-500 italic">Be the first to comment.</div>
                )}
              </div>
              <div className="mt-4 rounded-xl ring-1 ring-emerald-100 bg-white p-3">
                <input type="text" placeholder="Your name (optional)"
                       data-testid="civic-comment-name"
                       value={comment.author_name}
                       onChange={(e) => setComment({ ...comment, author_name: e.target.value })}
                       className="w-full text-sm border-0 focus:ring-0 focus:outline-none px-0 py-1" />
                <textarea rows={2} placeholder="Add a helpful comment or ask for verification…"
                          data-testid="civic-comment-text"
                          value={comment.text}
                          onChange={(e) => setComment({ ...comment, text: e.target.value })}
                          className="w-full text-sm border-0 focus:ring-0 focus:outline-none px-0 py-1 resize-none" />
                <div className="flex justify-end">
                  <button onClick={submitComment} disabled={submittingComment || !comment.text.trim()}
                          data-testid="civic-comment-submit"
                          className="rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-1.5 transition">
                    Post comment
                  </button>
                </div>
              </div>
            </section>
          </div>

          {/* Right column — map + status update panel */}
          <aside className="space-y-5">
            <div className="rounded-2xl overflow-hidden ring-1 ring-emerald-100 bg-white">
              {issue.location?.lat && (
                <CivicMap
                  issues={[issue]}
                  categoryColors={{ [issue.category]: statusStyle.color }}
                  height={220}
                  center={[issue.location.lat, issue.location.lng]}
                  zoom={15}
                  interactive={false}
                  fitToIssues={false}
                />
              )}
              <div className="p-3 text-xs text-slate-500">
                <div>📍 {issue.location?.address || `${issue.location?.city || ""}, ${issue.location?.state || ""}`}</div>
                <div>🌐 {issue.location?.lat?.toFixed(5)}, {issue.location?.lng?.toFixed(5)}</div>
              </div>
            </div>

            <div className="rounded-2xl ring-1 ring-emerald-100 bg-white p-4 text-sm">
              <div className="font-semibold text-slate-800 mb-2">Reported by</div>
              <div className="text-slate-700">
                {issue.anonymous ? <span className="italic">Anonymous citizen</span> : issue.reporter_name || "Citizen"}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{new Date(issue.created_at).toLocaleString()}</div>
            </div>

            {issue.assigned_authority_name && (
              <div className="rounded-2xl ring-1 ring-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="civic-assigned-authority">
                <div className="font-semibold text-emerald-800 mb-1 inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Assigned Authority
                </div>
                <div className="text-slate-800 font-medium">{issue.assigned_authority_name}</div>
                {issue.assigned_authority_id && (
                  <Link to={civicPath(`/authorities/${issue.assigned_authority_id}`)}
                        className="text-xs text-emerald-700 hover:underline mt-1 inline-block">
                    View their live inbox →
                  </Link>
                )}
              </div>
            )}

            <div className="rounded-2xl ring-1 ring-emerald-100 bg-white p-4 text-sm">
              <div className="font-semibold text-slate-800 mb-2">Update status</div>
              <p className="text-xs text-slate-500 mb-3">
                Moderators & authorities only. Public "resolved" claims still require community verification.
              </p>
              <select data-testid="civic-status-select" value={statusForm.status}
                      onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm mb-2">
                <option value="">Select new status…</option>
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <textarea rows={2} placeholder="Note (optional)"
                        data-testid="civic-status-note"
                        value={statusForm.note}
                        onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm resize-none mb-2" />
              <button onClick={submitStatus}
                      data-testid="civic-status-submit"
                      disabled={!statusForm.status}
                      className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-sm font-semibold py-2 transition">
                Update
              </button>
            </div>

            <div className="rounded-2xl ring-1 ring-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
              <AlertTriangle className="inline w-3.5 h-3.5 mr-1" />
              This is not an emergency service. If lives are in immediate danger, dial <b>112</b>.
            </div>
          </aside>
        </div>
      </div>
      {shareOpen && (
        <ShareModal
          onClose={() => setShareOpen(false)}
          data={shareData}
          title={issue.title}
        />
      )}
    </div>
  );
}

function ShareModal({ onClose, data, title }) {
  const url = data?.short_url || data?.canonical_url || window.location.href;
  const encoded = encodeURIComponent(url);
  const wa = `https://wa.me/?text=${encodeURIComponent(title + " — " + url)}`;
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encoded}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encoded}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy failed — long-press the field to copy manually.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="civic-share-modal"
         onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl relative"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
                data-testid="civic-share-close"
                className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100 text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <h3 className="font-serif text-xl font-bold text-slate-900 mb-1">Share this issue</h3>
        <p className="text-xs text-slate-500 mb-4">
          The more citizens rally behind it, the harder it is to ignore.
        </p>
        {data ? (
          <>
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 flex items-center justify-center mb-4">
              <img src={data.qr_url} alt={`QR code for ${title}`}
                   className="w-44 h-44 rounded-lg" data-testid="civic-share-qr" />
            </div>
            <div className="rounded-lg ring-1 ring-slate-200 flex items-center overflow-hidden mb-4">
              <input readOnly value={url} data-testid="civic-share-url"
                     className="flex-1 px-3 py-2 text-xs text-slate-700 bg-white focus:outline-none" />
              <button onClick={copy}
                      data-testid="civic-share-copy"
                      className="px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold inline-flex items-center gap-1">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs font-semibold">
              <a href={wa} target="_blank" rel="noreferrer"
                 data-testid="civic-share-whatsapp"
                 className="rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-center py-2 transition">
                WhatsApp
              </a>
              <a href={tw} target="_blank" rel="noreferrer"
                 className="rounded-lg bg-sky-100 hover:bg-sky-200 text-sky-800 text-center py-2 transition">
                Twitter
              </a>
              <a href={fb} target="_blank" rel="noreferrer"
                 className="rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-800 text-center py-2 transition">
                Facebook
              </a>
            </div>
            {!data.short_url && (
              <p className="mt-3 text-[11px] text-slate-400 italic">
                Short-URL will auto-upgrade to splink.pro once the CivicPulse tenant key is provisioned.
              </p>
            )}
          </>
        ) : (
          <div className="py-10 grid place-items-center">
            <Loader2 className="w-6 h-6 text-emerald-700 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
